import { useEffect, useState } from 'react'
import { fetchSportTradingEmails, updateSportTradingEmails } from './api'
import StringList from './StringList'
import { formatRelative, formatTsUtc } from './time'

interface Props {
  sportId: number
  // Display name — becomes the mailto subject line on COMPOSE EMAIL.
  sportName: string
  // Trading+ — same gate as the rest of the /trading page.
  canEdit: boolean
}

type Mode = 'view' | 'edit'

// Loose shape check: catches "not an email at all" (missing @, embedded
// whitespace, no dot in the domain), not RFC edge cases.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// EMAIL LIST — per-sport TO / CC / BCC recipient lists plus a compose
// shortcut. Same self-contained fetch + edit/save lifecycle as
// MarketsTradedPanel, with the trading-notes freshness line since the table
// tracks updated_by.
export default function TradingEmailsPanel({ sportId, sportName, canEdit }: Props) {
  const [to, setTo] = useState<string[]>([])
  const [cc, setCc] = useState<string[]>([])
  const [bcc, setBcc] = useState<string[]>([])
  const [baseline, setBaseline] = useState<{ to: string[], cc: string[], bcc: string[] }>({ to: [], cc: [], bcc: [] })
  const [meta, setMeta] = useState<{ updated_at: string | null, updated_by: string | null }>({ updated_at: null, updated_by: null })
  const [mode, setMode] = useState<Mode>('view')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err', text: string } | null>(null)

  // Re-fetch whenever the selected sport changes; reset any in-flight edit.
  useEffect(() => {
    let cancelled = false
    setMode('view')
    setMsg(null)
    setLoading(true)
    fetchSportTradingEmails(sportId)
      .then((data) => {
        if (cancelled) return
        setTo(data.to_emails)
        setCc(data.cc_emails)
        setBcc(data.bcc_emails)
        setBaseline({ to: data.to_emails, cc: data.cc_emails, bcc: data.bcc_emails })
        setMeta({ updated_at: data.updated_at, updated_by: data.updated_by })
      })
      .catch((e) => {
        if (!cancelled) setMsg({ kind: 'err', text: (e as Error).message })
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [sportId])

  function cancel() {
    setTo(baseline.to)
    setCc(baseline.cc)
    setBcc(baseline.bcc)
    setMode('view')
    setMsg(null)
  }

  async function save() {
    // Shape-validate before hitting the API — a bad address in a mailto list
    // silently breaks the whole compose link, so reject the save inline.
    const invalid = [...to, ...cc, ...bcc].filter(e => !EMAIL_RE.test(e))
    if (invalid.length > 0) {
      setMsg({ kind: 'err', text: `Not a valid email: ${invalid.join(', ')}` })
      return
    }
    setBusy(true)
    setMsg(null)
    try {
      await updateSportTradingEmails(sportId, { to_emails: to, cc_emails: cc, bcc_emails: bcc })
      // Re-fetch the canonical stored shape (and the fresh updated_at/by).
      const saved = await fetchSportTradingEmails(sportId)
      setTo(saved.to_emails)
      setCc(saved.cc_emails)
      setBcc(saved.bcc_emails)
      setBaseline({ to: saved.to_emails, cc: saved.cc_emails, bcc: saved.bcc_emails })
      setMeta({ updated_at: saved.updated_at, updated_by: saved.updated_by })
      setMode('view')
      setMsg({ kind: 'ok', text: 'Saved email lists.' })
    }
    catch (e) {
      setMsg({ kind: 'err', text: (e as Error).message })
    }
    finally {
      setBusy(false)
    }
  }

  function compose() {
    // mailto via location.href (not window.open) — hands off to the OS mail
    // handler without spawning a blank tab. cc/bcc params omitted when empty.
    // Each address is percent-encoded individually (commas stay raw as
    // separators): a `+` in a local part is a valid email but several
    // handlers — Gmail's browser handler notably — decode a raw `+` in the
    // query portion as a space.
    const params = [
      ...(cc.length > 0 ? [`cc=${cc.map(encodeURIComponent).join(',')}`] : []),
      ...(bcc.length > 0 ? [`bcc=${bcc.map(encodeURIComponent).join(',')}`] : []),
      `subject=${encodeURIComponent(sportName)}`,
    ]
    window.location.href = `mailto:${to.map(encodeURIComponent).join(',')}?${params.join('&')}`
  }

  const readOnly = mode === 'view'

  return (
    <div className="ad-editor-section">
      <div className="ad-section-head">
        <h3 className="b-mono">EMAIL LIST</h3>
        <div className="ad-section-actions">
          {readOnly && !loading && (
            <button
              type="button"
              className="b-btn b-btn-tiny"
              onClick={compose}
              disabled={to.length === 0}
              title={to.length === 0 ? 'Add a TO address first' : `Email ${to.length} recipient${to.length === 1 ? '' : 's'}`}
            >
              COMPOSE EMAIL →
            </button>
          )}
          {canEdit && !loading && (
            readOnly
              ? (
                  <button type="button" className="b-btn b-btn-tiny" onClick={() => setMode('edit')}>
                    EDIT
                  </button>
                )
              : (
                  <>
                    <button type="button" className="b-btn-ghost b-btn-tiny" onClick={cancel} disabled={busy}>
                      CANCEL
                    </button>
                    <button type="button" className="b-btn b-btn-primary b-btn-tiny" onClick={save} disabled={busy}>
                      {busy ? 'SAVING…' : 'SAVE'}
                    </button>
                  </>
                )
          )}
        </div>
      </div>

      {msg && (
        <p className={`ad-msg b-mono${msg.kind === 'err' ? ' is-err' : ' is-ok'}`}>{msg.text}</p>
      )}

      {loading
        ? <p className="b-mono-dim b-tiny">Loading…</p>
        : (
            <div className="ad-emails-grid">
              <StringList
                label="TO"
                values={to}
                onChange={setTo}
                readOnly={readOnly}
                placeholder="Add email…"
              />
              <StringList
                label="CC"
                values={cc}
                onChange={setCc}
                readOnly={readOnly}
                placeholder="Add email…"
              />
              <StringList
                label="BCC"
                values={bcc}
                onChange={setBcc}
                readOnly={readOnly}
                placeholder="Add email…"
              />
            </div>
          )}

      {readOnly && meta.updated_at && (
        <p className="ad-trading-note-meta b-mono-dim b-tiny">
          updated
          {' '}
          <span title={formatTsUtc(meta.updated_at)}>{formatRelative(meta.updated_at)}</span>
          {meta.updated_by && ` by ${meta.updated_by}`}
        </p>
      )}
    </div>
  )
}
